/** SQUAD Value factsheet — 4-page react-pdf template (static-first). */
import squadFondsLogoPng from '../../../../../assets/images/squad-fonds-logo.png';

export const TEMPLATE_ID_SQUAD = '6a56437d260e22f9e9296ede';

export const SQUAD_DEFAULT_ISIN = 'LU0199057307';

/** Brand palette from FactSheet Excel / mockup (gold + navy). */
export const SQUAD_COLORS = {
  gold: '#C4A35A',
  goldDark: '#A88B3F',
  goldLight: '#E8D9B0',
  goldRow: '#F5E6C8',
  goldRowAlt: '#EDE0C4',
  /** Dark text / navy headers — feedback: #0c1c3d */
  navy: '#0c1c3d',
  navySoft: '#1A3A5C',
  sidebarBg: '#CFB584',
  /** Page-2 portfolio charts (bars + pie gradient base) — same as sidebar bg. */
  chartPrimary: '#CFB584',
  /** Body / dark copy — same as navy per brand feedback. */
  text: '#0c1c3d',
  /** No longer used for footnotes — SQUAD wants one uniform text colour throughout. */
  muted: '#666666',
  white: '#FFFFFF',
  footnoteBg: '#F5E08A',
  barGold: '#C4A35A',
  barNavy: '#0c1c3d',
  barOrange: '#D4783A',
} as const;

/** react-pdf registered family name (see PdfPreviewDialog Font.register). */
export const SQUAD_PDF_FONT_FAMILY = 'SegoeUI';

/** Web / Chart.js CSS stack — Segoe UI first. */
export const SQUAD_FONT_STACK =
  'SegoeUI, "Segoe UI", system-ui, -apple-system, sans-serif';

export const SQUAD_FONT_FILES = {
  regular: '/fonts/SegoeUI-Regular.ttf',
  /**
   * Converted from SQUAD's webfont kit — the weight between Regular and Bold.
   * Its hhea/usWin metrics were normalised from 2374/874 to the 2210/514 that
   * Regular and Bold use: react-pdf places the baseline via hhea.ascent, so the
   * kit's values would sit the title 2.3 pt lower than any other text of the same
   * size and quietly calibrate the header height to that quirk.
   */
  semibold: '/fonts/SegoeUI-Semibold.ttf',
  bold: '/fonts/SegoeUI-Bold.ttf',
  italic: '/fonts/SegoeUI-Italic.ttf',
  boldItalic: '/fonts/SegoeUI-BoldItalic.ttf',
} as const;

export const SQUAD_FONDSDATEN_KEYS: Array<{ key: string; label: string }> = [
  { key: 'wkn', label: 'WKN' },
  { key: 'isin', label: 'ISIN' },
  { key: 'anteilklasse', label: 'Anteilklasse' },
  { key: 'vertriebsstatus', label: 'Vertriebsstatus' },
  { key: 'fondskategorie', label: 'Fondskategorie' },
  { key: 'mindestkapitalbeteiligungsquote', label: 'Mindestkapitalbeteiligungsquote' },
  { key: 'fondswaehrung', label: 'Fondswährung' },
  { key: 'mindestzeichnung', label: 'Mindestzeichnung' },
  { key: 'sparplanfaehig', label: 'Sparplanfähig' },
  { key: 'auflagedatum', label: 'Auflagedatum' },
  { key: 'erstausgabepreis', label: 'Erstausgabepreis' },
  { key: 'kursNav', label: 'Kurs (NAV)' },
  { key: 'anteilsklassenvolumen', label: 'Anteilsklassenvolumen' },
  { key: 'fondsvolumen', label: 'Fondsvolumen' },
  { key: 'ertragsverwendung', label: 'Ertragsverwendung' },
  // Moved up from "Weitere Daten" on page 2, in the order of SQUAD's layout document
  { key: 'fondsdomizil', label: 'Fondsdomizil' },
  { key: 'geschaeftsjahresende', label: 'Geschäftsjahresende' },
  { key: 'vertriebszulassung', label: 'Vertriebszulassung²' },
  { key: 'fondstyp', label: 'Fondstyp/Rechtsform' },
  { key: 'sfdr', label: 'Einstufung nach SFDR' },
];

/** Page 2, headed "Gebühren" — the three non-fee rows moved to SQUAD_FONDSDATEN_KEYS. */
/** Gebühren (Seite 2): Label-Spalte 55 % statt 60 % — sonst bricht „… 1,50 %“ um (Weekly 02.10.2026). */
export const SQUAD_GEBUEHREN_LABEL_WIDTH_PCT = 55;

export const SQUAD_WEITERE_DATEN_KEYS: Array<{ key: string; label: string }> = [
  { key: 'ausgabeaufschlag', label: 'Ausgabeaufschlag' },
  { key: 'ruecknahmeabschlag', label: 'Rücknahmeabschlag' },
  { key: 'ter', label: 'Laufende Kosten (TER)' },
  { key: 'verwaltungsgebuehr', label: '- davon Verwaltungsgebühr p.a.' },
  { key: 'erfolgsgebuehr', label: 'Erfolgsgebühr p.a.' },
];

/**
 * Identifiers and machine-read values — these stay single-line in the editor, because
 * a line break in them is a typo, never a layout choice.
 */
export const SQUAD_SINGLE_LINE_FONDSDATEN_KEYS = ['wkn', 'isin'];

/** Order, labels and defaults live with the block rendering in squadFondspartner.ts. */
export { SQUAD_FONDSPARTNER_KEYS } from './squadFondspartner';

export const SECTION_HEADER_DEFAULTS = {
  anlagestrategie: 'Anlagestrategie',
  wertentwicklungSeitBeratung: 'Wertentwicklung seit Beratungsübernahme am 01.04.2024',
  /** No date since 09.10.2026 — the page header carries „Werbemitteilung (Stand: …)“. */
  wertentwicklungRisiko: 'Wertentwicklung und Risikokennzahlen',
  monatskommentar: 'Monatskommentar',
  /** CellConfig tags: text + cixon_chart.datum (see sectionHeaderPlaceholders). */
  fondsdaten: 'Fondsdaten per {date}',
  fondsberater: 'Fondsberater',
  weitereDaten: 'Gebühren',
  fondspartner: 'Fondspartner',
  kontakt: 'Kontakt',
  allokationAktien: 'Allokation Aktien',
  allokationEvents: 'Allokation Events und Anleihen',
  assetAllokation: 'Asset Allokation und Marktkapitalisierungen',
  renditenPortfoliodaten: 'Renditen und Portfoliodaten',
  kalenderjahr: 'Wertentwicklung je Kalenderjahr',
  rollierend: 'Rollierende Wertentwicklung',
  historischeAssetAllokation: 'Historische Asset-Allokation',
  investmentprozess: 'Investmentprozess',
  zusammenfassung: 'Zusammenfassung',
  chancen: 'Chancen',
  risiken: 'Risiken',
  risikoIndikator: 'Risiko Indikator³',
  erlaeuterungen: 'Erläuterungen zu den Seiten 1 bis 3',
  wichtigeHinweise: 'Wichtige Hinweise',
};

/** Placeholder labels for image slots (assets delivered later). */
export const IMAGE_SLOT_LABELS = {
  ratingsSeals: 'Rating seals (Morningstar / Awards)',
  rollingPerformance: 'Rollierende Wertentwicklung — bar chart',
  historischeAssetAllokation: 'Historische Asset-Allokation — image placeholder',
  investmentprozess: 'Investmentprozess — image placeholder',
  zusammenfassung: 'Zusammenfassung — icons placeholder',
  page3Image: 'Bild unter „Rollierende Wertentwicklung“ — 1280 px breit, Überschriften im Bild',
  advisorPhoto1: 'Advisor photo 1',
  advisorPhoto2: 'Advisor photo 2',
  advisorPhoto3: 'Advisor photo 3',
  advisorLogo1: 'Advisor logo 1',
  advisorLogo2: 'Advisor logo 2',
  advisorLogoImage: 'Logo unter der Fondsberater-Beschreibung (Seite 1)',
  fondspartnerLogoImage: 'Optionales Logo unten (Seite 2)',
} as const;

/** Default rating seals strip for page 1 right panel. */
export const SQUAD_RATINGS_SEALS_IMAGE = '/images/squad/ratings-seals.png';

/** Factsheet header logo (transparent PNG; drop SVG at SQUAD_FONDS_LOGO_SVG_PUBLIC_PATH when available). */
/** @deprecated The wordmark is drawn as vector now — see SquadFondsLogo.tsx. */
export const SQUAD_FONDS_LOGO_PUBLIC_PATH = '/images/squad/squad-fonds-logo.png';
export const SQUAD_FONDS_LOGO_SVG_PUBLIC_PATH = '/images/squad/squad-fonds-logo.svg';

/** Webpack-bundled header logo for preview + react-pdf. */
export const SQUAD_FONDS_LOGO_ASSET: string = squadFondsLogoPng;
